import { styled } from '@mui/material';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Plugin } from '@tiptap/pm/state';
import { EditorContent, Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { memo, useEffect, useState } from 'react';
import Box from 'shell/components/Box';
import FormLabel from 'shell/components/FormLabel';
import RichTextToolbar from 'shell/components/RichTextToolbar';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { clipboardTextParser } from 'utils/helper';

const HELPER_TEXT_POSITIONS = ['start', 'end'];
const VARIANTS = ['static', 'dynamic'];

const replaceLineBreaksWithPTags = (value) => {
    let newValue = value;
    newValue = newValue.replaceAll('\n\n', '</p><p></p><p>');
    if (!newValue.startsWith('<')) {
        newValue = `<p>${newValue}</p>`;
    }
    return newValue;
};

const RichTextArea = memo(
    ({
        'aria-label': ariaLabel,
        'aria-describedby': ariaDescribedBy,
        id,
        defaultValue,
        disabled,
        error = false,
        helperText,
        helperTextPosition = 'start',
        label,
        placeholder = 'Type a message',
        readOnly,
        required,
        value,
        variant = 'static',
        onBlur,
        onChange,
        ...rest
    }) => {
        if (!HELPER_TEXT_POSITIONS.includes(helperTextPosition)) {
            throw new Error(`Shell: Invalid helperTextPosition "${helperTextPosition}" in component RichTextArea.`);
        }
        if (!VARIANTS.includes(variant)) {
            throw new Error(`Shell: Invalid variant "${variant}" in component RichTextArea.`);
        }

        const [showToolbar, setShowToolbar] = useState(false);
        const [isCreated, setIsCreated] = useState(false);
        const editor = useEditor({
            editorProps: {
                attributes: {
                    'aria-label': ariaLabel,
                    'aria-describedby': ariaDescribedBy,
                    role: 'textbox',
                },
                clipboardTextParser,
            },
            extensions: [
                Link.configure({
                    HTMLAttributes: {
                        target: '_blank',
                    },
                }),
                Placeholder.configure({
                    placeholder,
                    showOnlyWhenEditable: false,
                }),
                StarterKit.configure({
                    blockquote: false,
                    codeBlock: false,
                    heading: false,
                    horizontalRule: false,
                    strike: false,
                }),
                Underline,
                Extension.create({
                    addProseMirrorPlugins() {
                        return [
                            new Plugin({
                                props: {
                                    handleKeyDown: (_, e) => {
                                        if (e.key === 'Backspace') {
                                            if (this.editor.isActive('bulletList')) {
                                                if (!this.editor.view.state.selection.$cursor) return false;
                                                const listItemText =
                                                    this.editor.view.state.selection.$cursor.parent.textContent;
                                                if (!listItemText) {
                                                    this.editor.commands.toggleBulletList();
                                                    return true;
                                                }
                                            }

                                            if (this.editor.isActive('orderedList')) {
                                                if (!this.editor.view.state.selection.$cursor) return false;
                                                const listItemText =
                                                    this.editor.view.state.selection.$cursor.parent.textContent;
                                                if (!listItemText) {
                                                    this.editor.commands.toggleOrderedList();
                                                    return true;
                                                }
                                            }
                                        }
                                    },
                                },
                            }),
                        ];
                    },
                }),
            ],
            content: value ?? defaultValue,
            onCreate: ({ editor }) => {
                setIsCreated(true);
                editor.commands.setContent(replaceLineBreaksWithPTags(value ?? defaultValue), true, {
                    preserveWhitespace: 'full',
                });
            },
            onUpdate: ({ editor }) => {
                if (!isCreated || !onChange) return;
                const editorContent = editor.isEmpty ? '' : editor.getHTML().replaceAll('<br>', '</p><p>');
                if (value !== editorContent) onChange(editorContent);
            },
        });

        const handleFocus = () => {
            setShowToolbar(true);
        };

        const handleBlur = () => {
            setShowToolbar(false);
            onBlur && onBlur(editor.isEmpty ? '' : editor.getHTML().replaceAll('<br>', '</p><p>'));
        };

        useEffect(() => {
            if (!editor) return;
            const isEditable = !Boolean(disabled || readOnly);
            editor.setEditable(isEditable);
        }, [editor, disabled, readOnly]);

        useEffect(() => {
            if (isCreated) {
                editor.commands.setContent(replaceLineBreaksWithPTags(value ?? defaultValue), true, {
                    preserveWhitespace: 'full',
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isCreated]);

        useEffect(() => {
            if (!editor) return;
            if (value !== editor.getHTML()) {
                editor.commands.setContent(replaceLineBreaksWithPTags(value), true, {
                    preserveWhitespace: 'full',
                });
            }
        }, [editor, value]);

        return (
            <Stack {...rest}>
                {label && (
                    <CustomFormLabel required={required} onClick={() => editor.commands.focus()}>
                        {label}
                    </CustomFormLabel>
                )}
                <CustomStack
                    className={'MuiRichTextArea-root'}
                    disabled={disabled}
                    $error={error}
                    $showToolbar={showToolbar}
                    spacing={3}
                    variant={variant}
                >
                    {variant === 'static' ? (
                        <>
                            {!readOnly && <RichTextToolbar disabled={disabled} editor={editor} />}
                            <CustomBox disabled={disabled} variant={variant}>
                                <EditorContent
                                    data-cy={'richtextarea-content-input'}
                                    id={id}
                                    editor={editor}
                                    onBlur={handleBlur}
                                />
                            </CustomBox>
                        </>
                    ) : (
                        <>
                            <CustomBox disabled={disabled} variant={variant}>
                                <EditorContent id={id} editor={editor} onFocus={handleFocus} onBlur={handleBlur} />
                            </CustomBox>
                            {showToolbar && !(disabled || readOnly) && <RichTextToolbar editor={editor} />}
                        </>
                    )}
                </CustomStack>
                {helperText && (
                    <CustomText $error={error} $helperTextPosition={helperTextPosition} variant={'p3'}>
                        {helperText}
                    </CustomText>
                )}
            </Stack>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps['aria-label'] === nextProps['aria-label'] &&
            prevProps['aria-describedby'] === nextProps['aria-describedby'] &&
            prevProps.id === nextProps.id &&
            prevProps.defaultValue === nextProps.defaultValue &&
            prevProps.disabled === nextProps.disabled &&
            prevProps.error === nextProps.error &&
            prevProps.helperText === nextProps.helperText &&
            prevProps.helperTextPosition === nextProps.helperTextPosition &&
            prevProps.label === nextProps.label &&
            prevProps.placeholder === nextProps.placeholder &&
            prevProps.readOnly === nextProps.readOnly &&
            prevProps.required === nextProps.required &&
            prevProps.value === nextProps.value &&
            prevProps.variant === nextProps.variant
        );
    }
);

const CustomFormLabel = styled(FormLabel)(({ theme }) => {
    return {
        marginBottom: theme.spacing(1.25),
        color: theme.palette.ensightful.neutral[900],
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.ensightful.error[600],
        },
    };
});

const CustomStack = styled(Stack)(({ disabled, $error, $showToolbar, variant, theme }) => {
    return {
        position: 'relative',
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
        borderRadius: '16px',
        width: '100%',
        minHeight: '8rem',
        maxHeight: '20rem',
        height: 'auto',
        padding: theme.spacing(1.5, 2),
        background: theme.palette.ensightful.shades[0],
        overflow: 'hidden',
        cursor: 'text',
        '&:focus-within': {
            borderColor: theme.palette.ensightful.primary[700],
            outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
            outlineOffset: '2px',
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[700],
        },
        ...($error && {
            borderColor: theme.palette.ensightful.error[700],
            '&:focus-within': {
                borderColor: theme.palette.ensightful.error[700],
                outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
                outlineOffset: '2px',
            },
            '&:hover': {
                borderColor: theme.palette.ensightful.error[700],
            },
        }),
        ...(disabled && {
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            '&:focus-within': {
                outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
                outlineOffset: '2px',
            },
            '&:hover': {
                borderColor: theme.palette.ensightful.neutral[500],
            },
        }),
        ...(variant === 'dynamic' && {
            borderColor: 'transparent',
            background: 'transparent',
            '&:focus-within': {
                borderColor: theme.palette.ensightful.primary[700],
                outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
                outlineOffset: '2px',
            },
            '&:hover': {
                borderColor: theme.palette.ensightful.primary[700],
            },
            ...($showToolbar && {
                minHeight: '8rem',
            }),
        }),
    };
});

const CustomBox = styled(Box)(({ disabled, variant, theme }) => {
    return {
        flex: 1,
        display: 'flex',
        padding: variant === 'static' ? theme.spacing(0, 2) : 0,
        overflowY: 'auto',
        '& > div': {
            flex: 1,
        },
        '& p': {
            margin: 0,
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            lineHeight: '21px',
        },
        '& code': {
            fontFamily: 'monospace',
        },
        '& .ProseMirror': {
            height: '100%',
            overflowWrap: 'anywhere',
            '& ul, ol': {
                margin: 0,
                fontSize: '0.875rem',
            },
        },
        '& .ProseMirror-focused': {
            outline: 'none',
        },
        '& .ProseMirror p.is-editor-empty:first-child::before': {
            color: theme.palette.ensightful.neutral[500],
            content: 'attr(data-placeholder)',
            float: 'left',
            height: '0',
            pointerEvents: 'none',
            ...(disabled && {
                color: theme.palette.ensightful.neutral[400],
            }),
        },
    };
});

const CustomText = styled(Text)(({ $error, $helperTextPosition, theme }) => {
    const textAlign = {
        start: 'left',
        end: 'right',
    }[$helperTextPosition];

    return {
        marginTop: theme.spacing(1),
        textAlign,
        color: $error ? theme.palette.ensightful.error[700] : theme.palette.ensightful.neutral[700],
    };
});

export default RichTextArea;
